import type { Rate } from '../types';
import { formatCurrency } from '../utils/currency';

interface RateSelectorProps {
  rates: Rate[];
  selectedRateId: string | null;
  onSelect: (rate: Rate) => void;
  loading?: boolean;
  disabled?: boolean;
}

function RateSelector({ rates, selectedRateId, onSelect, loading = false, disabled = false }: RateSelectorProps) {
  if (loading) {
    return (
      <section className="form-section" aria-labelledby="rates-heading">
        <h2 id="rates-heading">Shipping Rates</h2>
        <div className="skeleton skeleton-text" style={{ height: '60px' }} />
        <div className="skeleton skeleton-text" style={{ height: '60px', marginTop: '10px' }} />
      </section>
    );
  }

  if (rates.length === 0) {
    return null;
  }

  const formatDelivery = (rate: Rate) => {
    const days = rate.est_delivery_days ?? rate.delivery_days;
    if (days === undefined || days === null) return '';
    if (days === 0) return 'Same day';
    if (days === 1) return '1 day';
    return `${days} days`;
  };

  return (
    <section className="form-section" aria-labelledby="rates-heading">
      <h2 id="rates-heading">Select Shipping Rate</h2>

      <div
        role="radiogroup"
        aria-labelledby="rates-heading"
        aria-disabled={disabled}
        style={{ display: 'grid', gap: '12px' }}
      >
        {rates.map((rate) => (
          <label
            key={rate.id}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '16px',
              padding: '16px',
              background:
                selectedRateId === rate.id
                  ? 'rgba(203, 112, 82, 0.1)'
                  : 'var(--champagne-dune)',
              border: `2px solid ${
                selectedRateId === rate.id
                  ? 'var(--desert-clay-dark)'
                  : 'transparent'
              }`,
              borderRadius: 'var(--radius-md)',
              cursor: disabled ? 'not-allowed' : 'pointer',
              opacity: disabled ? 0.75 : 1,
              transition: 'all var(--transition-fast)',
            }}
          >
            <input
              type="radio"
              name="rate"
              value={rate.id}
              checked={selectedRateId === rate.id}
              onChange={() => onSelect(rate)}
              disabled={disabled}
              style={{
                width: '20px',
                height: '20px',
                accentColor: 'var(--desert-clay-dark)',
              }}
            />
            <div style={{ flex: 1 }}>
              <div
                style={{
                  fontWeight: 600,
                  color: 'var(--copper-ember-dark)',
                  marginBottom: '4px',
                }}
              >
                {rate.carrier} - {rate.service}
              </div>
              {formatDelivery(rate) && (
                <div
                  style={{
                    fontSize: '0.9em',
                    color: 'var(--aged-mahogany-light)',
                  }}
                >
                  Est. delivery: {formatDelivery(rate)}
                </div>
              )}
            </div>
            <div
              style={{
                fontWeight: 700,
                fontSize: '1.2em',
                color: 'var(--copper-ember-dark)',
              }}
            >
              {formatCurrency(rate.rate, rate.currency)}
            </div>
          </label>
        ))}
      </div>
    </section>
  );
}

export default RateSelector;
